import {
  type ReactElement,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  formatHourOfDay,
  scheduleTargets,
  setSchedulePaused,
} from './scheduleWatchAgents';
import {
  WatchTowerShell,
} from './TargetedMarketingChrome';
import useWatchTowerMock from './useWatchTowerMock';
import WatchTowerCreateScheduleDialog from './WatchTowerCreateScheduleDialog';
import { formatAgentType, formatIsoDate } from './watchTowerSelectors';

import type { WatchCompany } from './types';

import styles from './scss/TargetedMarketing.module.scss';

function companyName(companies: WatchCompany[], id: number): string {
  return companies.find((item) => item.id === id)?.name || String(id);
}

function WatchTowerSchedules(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const {
    state, update, liveActions,
  } = useWatchTowerMock();
  const presetCompanyId = Number(params.get('companyId')) || 0;
  const [wizardOpen, setWizardOpen] = useState(() => Boolean(presetCompanyId));

  const visibleCompanies = useMemo(
    () => state.watchCompanies.filter((company) => company.status !== 'archived'),
    [state.watchCompanies],
  );
  const visibleSchedules = useMemo(
    () => state.schedules.filter((item) => !item.watchTopicId),
    [state.schedules],
  );
  const presetExists = visibleCompanies.some((item) => item.id === presetCompanyId);

  const onPause = (scheduleId: number, nextPaused: boolean) => {
    update((prev) => setSchedulePaused(prev, scheduleId, nextPaused));
    toast.success(nextPaused
      ? t('targetedMarketing.schedulePaused')
      : t('targetedMarketing.scheduleResumed'));
  };

  const onRunNow = () => {
    toast.error(t('targetedMarketing.runNowNotLive'));
  };

  return (
    <WatchTowerShell
      primary="runs"
      runsSubView="scheduler"
      actions={(
        <Button
          title={t('targetedMarketing.createSchedule')}
          startIcon={<PlusIcon />}
          onClick={() => setWizardOpen(true)}
        />
      )}
    >
      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.allSchedules')}</h2>
        {visibleSchedules.length === 0 ? (
          <EmptyList text={t('targetedMarketing.emptySchedules')}>
            <Button
              title={t('targetedMarketing.createSchedule')}
              startIcon={<PlusIcon />}
              onClick={() => setWizardOpen(true)}
            />
          </EmptyList>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('targetedMarketing.title')}</th>
                  <th>{t('targetedMarketing.companyColumn')}</th>
                  <th>{t('targetedMarketing.agents')}</th>
                  <th>{t('targetedMarketing.template')}</th>
                  <th>{t('targetedMarketing.frequency')}</th>
                  <th>{t('targetedMarketing.hourOfDay')}</th>
                  <th>{t('targetedMarketing.timezone')}</th>
                  <th>{t('targetedMarketing.targetsCount')}</th>
                  <th>{t('targetedMarketing.status')}</th>
                  <th>{t('targetedMarketing.nextRun')}</th>
                  <th>{t('targetedMarketing.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleSchedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>{schedule.title}</td>
                    <td>{companyName(state.watchCompanies, schedule.watchCompanyId)}</td>
                    <td>{formatAgentType(schedule.type)}</td>
                    <td>{schedule.templateName}</td>
                    <td>{t(`targetedMarketing.frequency_${schedule.frequency}`)}</td>
                    <td>{formatHourOfDay(schedule.hourOfDay)}</td>
                    <td>{schedule.timezone}</td>
                    <td>{scheduleTargets(schedule).length}</td>
                    <td>
                      {schedule.paused
                        ? t('targetedMarketing.paused')
                        : (schedule.lastRunStatus || t('targetedMarketing.none'))}
                    </td>
                    <td>{formatIsoDate(schedule.nextRunAt)}</td>
                    <td>
                      <div className={styles.actions}>
                        <Button
                          title={schedule.paused
                            ? t('targetedMarketing.resume')
                            : t('targetedMarketing.pause')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => onPause(schedule.id, !schedule.paused)}
                        />
                        <Button
                          title={t('targetedMarketing.runNow')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => onRunNow()}
                        />
                        <Button
                          title={t('targetedMarketing.openRuns')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => navigate(
                            `${AppRoutes.watchTowerRuns}?scheduleId=${schedule.id}`,
                          )}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <WatchTowerCreateScheduleDialog
        open={wizardOpen}
        presetCompanyId={presetExists ? presetCompanyId : 0}
        state={state}
        onClose={() => setWizardOpen(false)}
        onApply={update}
        onLiveSave={async (input) => {
          await liveActions.createSchedules(input, state);
        }}
      />
    </WatchTowerShell>
  );
}

export default WatchTowerSchedules;
